import { utf8 } from '@47ng/codec'
import { versionHeader } from 'client/services/api'
import {
  ClientDatabase,
  createClientDatabaseInstance,
  EventRow
} from 'client/state/stores/clientDatabase'
import { expose } from 'comlink'
import Dexie from 'dexie'
import { decryptString, parseSecretKey } from 'modules/crypto/box'
import { unpadJsonString } from 'modules/crypto/padding'
import type { DownlinkPayload } from 'server/plugins/pubsub'
import type { DownlinkWorkerInterface } from './defs'

const downlinkWorker: DownlinkWorkerInterface = {
  state: {
    status: 'idle',
    receivedMessagesCount: 0
  },
  projects: [],
  database: createClientDatabaseInstance(),
  stream: null,
  abortController: null,
  callbacks: {
    state: () => {},
    error: console.error
  },
  setup(callbacks) {
    this.callbacks = callbacks
  },
  async start(projects) {
    if (this.isRunning) {
      await this.stop()
    }
    console.debug(
      `[⚙️📥 ${self.name}]  ▶️ start${
        projects ? ` with projects: ${projects.map(p => p.id).join(',')}` : ''
      }`
    )
    this.state.status = 'starting'
    this.state.receivedMessagesCount = 0
    this.callbacks.state(this.state)
    if (projects) {
      this.projects = projects.map(project => {
        const secretKey = parseSecretKey(project.secretKey)
        return {
          projectID: project.id,
          decrypt(message) {
            return decryptString(message, secretKey)
          }
        }
      })
    }
    const db = await this.database // todo: Handle db opening errors here
    const cursor = await getCursor(db)
    const projectIDs = this.projects.map(p => p.projectID).join(',')
    console.debug(
      `[⚙️📥 ${self.name}] Fetching after ${cursor} (${new Date(
        cursor
      ).toISOString()})`
    )

    this.abortController = new AbortController()

    this.state.status = 'fetching'
    this.callbacks.state(this.state)
    const res = await fetch(
      `/api/downlink?projects=${projectIDs}&after=${cursor}`,
      {
        headers: {
          ...versionHeader,
          accept: 'application/vnd.chiffre-downlink-v1'
        },
        signal: this.abortController.signal
      }
    )

    const stream = res.body?.getReader()
    if (!stream) {
      // todo: Reconnection? Report?
      this._handleError(new Error('No stream available'))
      this.abortController.abort('No stream available')
      this.abortController = null
      this.state.status = 'stopped'
      this.callbacks.state(this.state)
      return
    }
    this.stream = stream
    this.stream.closed.catch(error => this._handleError(error))
    let processedCount = 0
    let previousPartialLine: string = ''

    function partialParse(line: string): DownlinkPayload | null {
      try {
        return JSON.parse(line)
      } catch {
        previousPartialLine = line
        console.debug(`[⚙️📥 ${self.name}] Storing partial line: ${line}`)
        return null
      }
    }

    setTimeout(() => {
      if (this.state.status === 'fetching') {
        // When there is nothing to catch up with and no live events yet,
        // switch automatically to live after 1 second.
        this.state.status = 'live'
        this.callbacks.state(this.state)
      }
    }, 1000)

    while (true) {
      const { done, value } = await stream.read()
      if (done) {
        break
      }
      const buffer = utf8.decode(value)
      const lines = buffer.trimEnd().split('\n')
      console.debug(`[⚙️📥 ${self.name}] Processing ${lines.length} messages`)
      const errors: any[] = []
      for (let line of lines) {
        if (!line.startsWith('{')) {
          console.debug(
            `[⚙️📥 ${self.name}] Reassembling partial line: ${previousPartialLine} + ${line}`
          )
          line = previousPartialLine + line
          previousPartialLine = ''
        }
        const downlinkPayload = partialParse(line)
        if (!downlinkPayload) {
          continue
        }
        const {
          projectID,
          ciphertext,
          country,
          id,
          timestamp,
          source,
          progress
        } = downlinkPayload
        const project = this.projects.find(p => p.projectID === projectID)
        if (!project) {
          continue
        }
        try {
          const cleartext = project.decrypt(ciphertext)
          const unpadded = unpadJsonString(cleartext)
          const event = JSON.parse(unpadded)
          const row: EventRow = {
            ...event,
            id,
            projectID,
            time: timestamp,
            country: country || '--'
          }
          // if ((progress?.count ?? 0) % 100 === 0) {
          //   console.dir(row)
          // }
          await db.events.add(row)
          processedCount++
        } catch (error) {
          errors.push(error)
          if (error instanceof Dexie.ConstraintError) {
            // Overlap is expected
          } else {
            this._handleError(error)
          }
        }
        this.state.status =
          source === 'past'
            ? 'catchingUp'
            : source === 'live'
            ? 'live'
            : this.state.status
        this.state.progress = progress
        this.state.receivedMessagesCount++
        if (progress && progress.count === progress.total) {
          this.state.status = 'live'
          this.state.progress = undefined
          console.debug(
            `[⚙️📥 ${self.name}] Caught up with past events (${processedCount} processed over ${progress.total} received)`
          )
        }
        this.callbacks.state(this.state)
      }
      if (errors.length) {
        console.error(errors)
      }
    }

    console.debug(`[⚙️📥 ${self.name}] Stream terminated`)
    // todo: Reconnection strategy
    this.stream = null
    this.abortController = null
    this.state.status = 'stopped'
    this.callbacks.state(this.state)
  },
  async stop() {
    console.debug(`[⚙️📥 ${self.name}]  ⏹ stop`)
    await this.stream?.cancel()
    this.abortController?.abort()
    this.stream = null
    this.abortController = null
    this.state.status = 'stopped'
    this.callbacks.state(this.state)
  },
  notify() {
    this.callbacks.state(this.state)
  },
  get isRunning() {
    return Boolean(this.stream)
  },

  // Internal --

  /**
   * Wrap errors into a structured-cloneable format
   * and report back to the main thread.
   * https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript
   */
  _handleError(error: unknown) {
    console.error(`[⚙️📥 ${self.name}]  🚨 ${String(error)}`)
    if (error instanceof Error) {
      return this.callbacks.error({
        name: error.name,
        message: error.message
      })
    }
    return this.callbacks.error({
      name: 'UnknownError',
      message: String(error)
    })
  }
}

expose(downlinkWorker)

export async function getCursor(db: ClientDatabase): Promise<number> {
  try {
    const results = await db.events.orderBy('time').reverse().limit(1).keys()
    if (results.length === 0) {
      return 0
    }
    return new Date(results[0] as any).valueOf()
  } catch (error) {
    console.error(error)
    return 0
  }
}
